import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../api";
import DataTable from "../components/DataTable";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtDate, fmtMoney, fmtNum, todayISO } from "../utils/format";

export default function Maintenance() {
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ vehicle_id: "", date: todayISO(), mileage: "", type: "Vidange", garage: "", parts: "", labor_cost: "", parts_cost: "", next_date: "", next_mileage: "", invoice_ref: "", account_id: "", notes: "" });

  const load = () => api.get("/maintenance").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/vehicles").then((r) => setVehicles(r.data));
    api.get("/accounts").then((r) => setAccounts(r.data));
    api.get("/meta").then((r) => setMeta(r.data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.vehicle_id) return toast.error("Véhicule requis");
    try {
      await api.post("/maintenance", { ...form, labor_cost: Number(form.labor_cost) || 0, parts_cost: Number(form.parts_cost) || 0, mileage: form.mileage ? Number(form.mileage) : null, next_mileage: form.next_mileage ? Number(form.next_mileage) : null, next_date: form.next_date || null, account_id: form.account_id || null });
      toast.success("Entretien enregistré et ajouté au coût de revient du véhicule");
      setOpen(false);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-5" data-testid="maintenance-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Entretiens</h1>
          <p className="mt-1 text-sm text-slate-500">Chaque entretien est automatiquement ajouté au coût réel du véhicule.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-maintenance-page-button"><Plus className="mr-1 h-4 w-4" /> Nouvel entretien</Button></DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Nouvel entretien</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Véhicule *</Label>
                <Select value={form.vehicle_id} onValueChange={(v) => set("vehicle_id", v)}>
                  <SelectTrigger data-testid="maint-vehicle-select"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.internal_number} — {v.brand} {v.model}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="maint-form-date" /></div>
              <div><Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => set("type", v)}>
                  <SelectTrigger data-testid="maint-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{(meta?.maintenance_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Kilométrage</Label><Input type="number" value={form.mileage} onChange={(e) => set("mileage", e.target.value)} /></div>
              <div><Label>Garage</Label><Input value={form.garage} onChange={(e) => set("garage", e.target.value)} data-testid="maint-form-garage" /></div>
              <div><Label>Main-d'œuvre (FCFA)</Label><Input type="number" value={form.labor_cost} onChange={(e) => set("labor_cost", e.target.value)} data-testid="maint-form-labor" /></div>
              <div><Label>Pièces (FCFA)</Label><Input type="number" value={form.parts_cost} onChange={(e) => set("parts_cost", e.target.value)} data-testid="maint-form-parts" /></div>
              <div className="col-span-2"><Label>Pièces utilisées</Label><Input value={form.parts} onChange={(e) => set("parts", e.target.value)} /></div>
              <div><Label>Prochain entretien</Label><Input type="date" value={form.next_date} onChange={(e) => set("next_date", e.target.value)} data-testid="maint-form-next" /></div>
              <div><Label>Prochain km</Label><Input type="number" value={form.next_mileage} onChange={(e) => set("next_mileage", e.target.value)} /></div>
              <div><Label>N° facture</Label><Input value={form.invoice_ref} onChange={(e) => set("invoice_ref", e.target.value)} /></div>
              <div><Label>Compte débité</Label>
                <Select value={form.account_id} onValueChange={(v) => set("account_id", v === "none" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Aucun</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="maint-form-submit">Enregistrer</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <DataTable testidPrefix="maintenance-table" rows={rows}
        searchKeys={["vehicle_label", "type", "garage"]}
        columns={[
          { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
          { key: "vehicle_label", label: "Véhicule", render: (r) => <span className="font-medium">{r.vehicle_label}</span> },
          { key: "type", label: "Type" },
          { key: "garage", label: "Garage", render: (r) => r.garage || "—" },
          { key: "mileage", label: "Km", render: (r) => fmtNum(r.mileage) },
          { key: "cost", label: "Coût", className: "text-right", render: (r) => <span className="font-mono-num font-semibold">{fmtMoney(r.cost)}</span> },
          { key: "next_date", label: "Prochain", render: (r) => r.next_date ? `${fmtDate(r.next_date)}${r.next_mileage ? ` / ${fmtNum(r.next_mileage)} km` : ""}` : "—" },
          { key: "created_by", label: "Par", render: (r) => <span className="text-xs text-slate-500">{r.created_by}</span> },
        ]}
      />
    </div>
  );
}
